/**
 * File: article-redirects.ts
 *
 * The addresses the articles had before they moved to Skapedia (2026-09-29),
 * and where each one is now. Skapedia names its files differently, so every
 * article's address changed with the move: `/articles/2026-08-13` became
 * `/articles/2026-08-13-00`. The old addresses are in search results, in links
 * people shared, and in links inside the articles themselves.
 *
 * It is a closed list: an article published after the move only ever had its
 * Skapedia address. The pairs were matched by article title on 2026-09-29.
 *
 * Imported in TWO places, so it imports nothing:
 *   - at RUNTIME by articles.ts: the article view sends a visitor who opens an
 *     old address to the new one;
 *   - at BUILD time by build/seo-plugins.ts: each old address gets a static
 *     page that points search engines and link previews at the new one.
 *
 * A value is the article's slug on this site, or a full `https://` address
 * when the article now lives on another site. A target that Skapedia does not
 * list (not published, or dated after today) is not redirected to: the old
 * address then answers "Article not found" like any unknown one.
 */

export const ARTICLE_REDIRECTS: Record<string, string> = {
    "2026-07-08": "2026-07-08-00", // BunnyQuery is now in public BETA!
    "2026-07-09": "2026-07-09-00", // v1.1.0 Bug fixes
    "2026-07-27": "2026-07-27-00", // v1.8.0 BETA
    "2026-08-13": "2026-08-13-00", // What is BunnyQuery?
    "2026-08-14": "2026-09-16-00", // The free tier just became bigger
    "2026-08-15": "2026-08-15-00", // Why BunnyQuery is not built on a vector database
    "2026-09-16": "2026-09-16-01", // New limits for your BunnyQuery data, now "New plan limits, and what happens past them"
    "2026-09-29": "2026-09-29-00", // v1.11.0: safer data, cleaner deletes

    // Skapi's own articles. This site carried them too; they are not among the
    // BunnyQuery articles in Skapedia, so each leads to the article on
    // www.skapi.com.
    "2026-08-12": "https://www.skapi.com/articles/2026-08-12-00", // Skapi 2.0 is out as a release candidate
    "2026-08-16": "https://www.skapi.com/articles/2026-08-16-00", // How Skapi saves you huge on AI tokens
    "2026-09-07-01": "https://www.skapi.com/articles/2026-09-04-00", // Skapi 2.0 is out
    "2026-09-07-02": "https://www.skapi.com/articles/2026-09-06-00", // How Skapi Encrypts Your Users' Data
    "2026-09-07-03": "https://www.skapi.com/articles/2026-09-06-01", // Bandwidth is on us, and Standard just got roomier
    "2026-09-07-05": "https://www.skapi.com/articles/2026-09-07-00", // Bonjour, Paris

    // "Your Data Should Answer You" (2026-09-07-04) is in neither list, so its
    // old address has nowhere to lead.
};

/** True for a target on another site (a full address), false for a slug on this one. */
export function isOtherSite(target: string): boolean {
    return /^https:\/\//.test(target);
}

/**
 * Where the old address `slug` leads, or `null` when it never was one. Own
 * keys only: "constructor" and the like are not addresses.
 */
export function redirectOf(slug: string): string | null {
    return Object.prototype.hasOwnProperty.call(ARTICLE_REDIRECTS, slug) ? ARTICLE_REDIRECTS[slug] : null;
}
